import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MoreVertical, Sun, Moon, FilePenLine, FileText, LogIn, LogOut, Zap, Crown, Settings, ArrowUpRight } from "lucide-react";
import { getPointsSummary, PLAN_CONFIG, type Plan } from "../../lib/credits-store";
import { useSettings } from "../../contexts/SettingsContext";
import type { TemplateMetaItem } from "../../hooks/useCvState";
import { cn } from "@/lib/utils";

interface MobileHeaderProps {
  mobileMenuRef: React.RefObject<HTMLDivElement | null>;
  logoMenuButtonRef: React.RefObject<HTMLButtonElement | null>;
  logoMenuPosition: { top: number; left: number } | null;
  mobileMenuOpen: boolean;
  mobileTab: 'edition' | 'ats' | 'preview';
  mobilePreviewSubTab: 'apercu' | 'visuel';
  theme: 'light' | 'dark';
  locale: 'fr' | 'en';
  authUser: { email?: string } | null;
  cvVersionCount: number;
  templateMeta: TemplateMetaItem[];
  activeTemplate: string;
  setActiveTemplate: (id: string) => void;
  onLogoMenuToggle: () => void;
  onThemeChange: (theme: 'light' | 'dark') => void;
  onLocaleChange: (locale: 'fr' | 'en') => void;
  onMobileTabChange: (tab: 'edition' | 'ats' | 'preview') => void;
  onMobilePreviewSubTabChange: (tab: 'apercu' | 'visuel') => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  t: (key: string) => string;
}

const PLAN_BADGE: Record<Plan, { label: string; className: string }> = {
  free:    { label: 'Free', className: 'bg-white/10 text-white/50' },
  mini:    { label: 'Mini', className: 'bg-sky-500/20 text-sky-300 border border-sky-500/30' },
  premium: { label: 'Premium', className: 'bg-amber-500/20 text-amber-300 border border-amber-500/30' },
};

export function MobileHeader({
  mobileMenuRef,
  logoMenuButtonRef,
  logoMenuPosition,
  mobileMenuOpen,
  mobileTab,
  mobilePreviewSubTab: _mobilePreviewSubTab,
  theme,
  locale: _locale,
  authUser,
  cvVersionCount,
  templateMeta,
  activeTemplate,
  setActiveTemplate,
  onLogoMenuToggle,
  onThemeChange,
  onLocaleChange,
  onMobileTabChange,
  onMobilePreviewSubTabChange,
  onLoginClick,
  onLogoutClick,
  t,
}: MobileHeaderProps) {
  const { openSettings } = useSettings();
  const navigate = useNavigate();
  const summary = getPointsSummary();
  const plan = summary.plan;
  const planCfg = PLAN_CONFIG[plan];
  const badge = PLAN_BADGE[plan];

  return (
    <header className="flex-shrink-0 fixed top-0 left-0 right-0 z-[9999] flex items-center gap-1 bg-[var(--app-bg,#0a0a0a)] border-b border-[var(--border-subtle,rgba(255,255,255,0.15))] px-2 py-1.5 pt-[max(0.375rem,env(safe-area-inset-top))]">
      <div className="relative flex items-center shrink-0 mr-1" ref={mobileMenuRef}>
        <button
          ref={logoMenuButtonRef}
          type="button"
          onClick={onLogoMenuToggle}
          className="flex items-center gap-1.5 min-w-0 rounded-lg py-1.5 pr-1 touch-manipulation min-h-[44px] text-left hover:bg-white/10 transition-colors w-full"
          aria-expanded={mobileMenuOpen}
          aria-haspopup="true"
          aria-label={t('sections.options')}
        >
          <div className="logo-crop w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
            <img src={theme === 'dark' ? '/logo-square-dark.png' : '/logo-square-light.png'} alt="ATSmyCV" className="w-full h-full object-cover" />
          </div>
          <MoreVertical size={20} className="shrink-0 text-white/70 hover:text-white" aria-hidden />
        </button>
        {mobileMenuOpen && logoMenuPosition && createPortal(
          <div
            data-portal="logo-menu"
            className="fixed py-2 rounded-xl border border-[var(--border-subtle,rgba(255,255,255,0.15))] bg-[var(--app-bg,#0a0a0a)] shadow-xl min-w-[240px] z-[99999] max-h-[80dvh] overflow-y-auto"
            style={{ top: logoMenuPosition.top, left: logoMenuPosition.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Account section ── */}
            {authUser ? (
              <div className="px-3 pt-2 pb-3 border-b border-white/10">
                <div className="text-xs text-white/50 truncate mb-1.5">{authUser.email}</div>
                <button type="button" onClick={() => { onLogoMenuToggle(); setTimeout(() => navigate('/pricing'), 50); }}
                  className="flex items-center gap-2 mb-2 w-full text-left touch-manipulation">
                  <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider', badge.className)}>
                    {badge.label}
                  </span>
                  {plan !== 'premium' ? (
                    <span className="text-[10px] text-white/40">
                      {cvVersionCount} CV{cvVersionCount !== 1 ? 's' : ''} · {planCfg.cvVersionsMax !== null ? `${planCfg.cvVersionsMax} max` : '∞'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] text-amber-400/70">
                      <Crown size={10} /> {t('userMenu.unlimited')}
                    </span>
                  )}
                </button>
                {/* Credits bar */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-[10px] font-semibold text-white/60">
                    <Zap size={11} className="text-yellow-400" />
                    {t('userMenu.credits')}
                  </div>
                  {plan === 'premium' ? (
                    <span className="text-[10px] text-amber-400 font-bold">{t('userMenu.unlimited')}</span>
                  ) : (
                    <span className="text-[10px] text-white/50 font-mono">
                      {summary.remaining} / {summary.totalAvailable} pts
                    </span>
                  )}
                </div>
                {plan !== 'premium' && summary.totalAvailable !== null && (
                  <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        summary.usagePercent >= 90 ? 'bg-red-500' :
                        summary.usagePercent >= 70 ? 'bg-amber-500' :
                        'bg-violet-500',
                      )}
                      style={{ width: `${summary.usagePercent}%` }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="px-3 py-2 border-b border-white/10">
                <button type="button" onClick={onLoginClick} className="w-full flex items-center gap-2 px-2 py-2.5 text-sm text-blue-400 hover:bg-white/10 rounded-lg touch-manipulation min-h-[44px]">
                  <LogIn size={16} /> {t('auth.loginTitle')}
                </button>
              </div>
            )}

            {/* ── Actions ── */}
            {authUser && (
              <div className="border-b border-white/10">
                {plan !== 'premium' && (
                  <button type="button" onClick={() => { onLogoMenuToggle(); setTimeout(() => navigate('/pricing'), 50); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-amber-400 hover:bg-white/10 touch-manipulation min-h-[40px]">
                    <ArrowUpRight size={15} /> {plan === 'free' ? t('userMenu.upgradeFreeMini') : t('userMenu.upgradeMini')}
                  </button>
                )}
                <button type="button" onClick={() => { onLogoMenuToggle(); openSettings(); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white/80 hover:bg-white/10 touch-manipulation min-h-[40px]">
                  <Settings size={15} /> {t('sections.options')}
                </button>
                <button type="button" onClick={onLogoutClick}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-white/10 touch-manipulation min-h-[40px]">
                  <LogOut size={15} /> {t('userMenu.logout')}
                </button>
              </div>
            )}

            {/* ── Theme ── */}
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase text-white/30 tracking-wider mt-1">{t('theme.light')} / {t('theme.dark')}</div>
            <button type="button" onClick={() => { onThemeChange('light'); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/10 touch-manipulation min-h-[40px]">
              <Sun size={15} /> {t('theme.light')}
            </button>
            <button type="button" onClick={() => { onThemeChange('dark'); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/10 touch-manipulation min-h-[40px]">
              <Moon size={15} /> {t('theme.dark')}
            </button>
            <div className="border-t border-white/10 my-1" />
            {/* ── Locale ── */}
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase text-white/30 tracking-wider">{t('locale.fr')} / {t('locale.en')}</div>
            <button type="button" onClick={() => { onLocaleChange('fr'); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/10 touch-manipulation min-h-[40px]">
              {t('locale.fr')}
            </button>
            <button type="button" onClick={() => { onLocaleChange('en'); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/10 touch-manipulation min-h-[40px]">
              {t('locale.en')}
            </button>
          </div>,
          document.body
        )}
      </div>
      {/* CV version selector — visible only in CV/preview mode, takes available space */}
      {templateMeta.length > 1 && mobileTab !== 'edition' ? (
        <div className="flex-1 min-w-0 flex items-center">
          <select
            value={activeTemplate}
            onChange={(e) => setActiveTemplate(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white/90 font-semibold truncate touch-manipulation min-h-[40px]"
            aria-label="CV"
          >
            {templateMeta.map((tm) => (
              <option key={tm.id} value={tm.id}>{tm.name}</option>
            ))}
          </select>
        </div>
      ) : (
        <div className="flex-1" />
      )}
      {/* Toggle: Edition ↔ CV (single button that alternates) */}
      <nav className="flex items-center gap-1.5 shrink-0 justify-end" aria-label={t('nav.ariaNav')}>
        <button
          type="button"
          onClick={() => {
            if (mobileTab === 'edition') {
              onMobileTabChange('preview');
              onMobilePreviewSubTabChange('apercu');
            } else {
              onMobileTabChange('edition');
            }
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold uppercase transition-colors touch-manipulation min-h-[44px] shrink-0 bg-blue-600 text-white"
        >
          {mobileTab === 'edition' ? (
            <><FileText size={14} aria-hidden /> <span>CV</span></>
          ) : (
            <><FilePenLine size={14} aria-hidden /> <span>{t('nav.edit')}</span></>
          )}
        </button>
      </nav>
    </header>
  );
}
